import {
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import {
	FormControl,
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';

import { Observable, of, Subject, Subscription } from 'rxjs';
import {
	debounceTime,
	distinctUntilChanged,
	filter,
	map,
	startWith,
	switchMap,
	takeUntil,
} from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { AddressConstants } from '../../../../modules/address/address.constants';
import { OrganizationsService } from '../../../../modules/organizations/organizations.service';
import { PaginationArgs } from '../../../../shared/components/pagination/models/pagination-args.model';
import { SortingType } from '../../../../shared/enums/sorting-type.enum';
import {
	HeadquarterSummary,
	NewOrganizationRequest,
} from '../../models/organizations.model';
import { ProductsSummary } from '../../models/products.model';
import {
	createOrgStart,
	editOrgRegStart,
	fetchAddressByCEPStart,
	fetchCnpjAvailabilityStart,
	fetchHeadquartersStart,
	fetchOrgProducts,
	fetchOrgRegStart,
	fetchUFCitiesClear,
	fetchUFCitiesStart,
	restoreFormReset,
} from '../../state/organizations.actions';
import {
	selectAddressResult,
	selectCities,
	selectCnpjAvailability,
	selectOrgProducts,
	selectOrgRegistration,
	selectRestoreForm,
} from '../../state/organizations.selector';

import { ProductsModalComponent } from './products-modal/products-modal.component';

@Component({
	selector: 'app-org-registration',
	styleUrls: ['./org-registration.component.scss'],
	templateUrl: './org-registration.component.html',
})
export class OrgRegistrationComponent implements OnInit, OnDestroy {
	@ViewChild('ipick')
	ipick!: ElementRef;

	addForm: UntypedFormGroup;
	auxStateForm: boolean = false;
	cepPattern = '[0-9]{8}';
	cities: string[] = [];
	cnpjPattern = '[0-9]{14}';
	cttForm: UntypedFormGroup;
	filteredHeadquarters$: Observable<HeadquarterSummary[]> = of([]);
	headquarters: HeadquarterSummary[] = [];
	inputId: string = String(Math.random());
	isLoading: boolean = true;
	matrizDisplayCtrl = new FormControl<HeadquarterSummary | string | null>('');

	orgId: string = '';
	organizations$: Subscription | null = null;

	products: ProductsSummary[] = [];
	productsForm: UntypedFormGroup;

	productsIds: string[] = [];
	productsInput: string = '';
	reconciliationReturnUrl: string | null = null;
	regForm: UntypedFormGroup;

	showEconomicGroup: boolean = true;
	showHintIdx: number = -1;
	uf = AddressConstants.ufLabels;

	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly fb: UntypedFormBuilder,
		private readonly activatedRoute: ActivatedRoute,
		private readonly store: Store,
		private readonly dialog: MatDialog,
		private readonly organizationsService: OrganizationsService,
	) {
		this.regForm = this.fb.group({
			cnpj: [null, [Validators.required, Validators.pattern(this.cnpjPattern)]],
			insurancePolicyNumber: [null],
			is_matriz: [true, [Validators.required]],
			matriz_id: [null],
			municipal_registration: [null],
			name: [null, [Validators.required]],
			name_economic_group: [null],
			name_secondary: [null, [Validators.required]],
			state_registration: [null],
			subinvoiceNumber: [null],
		});
		this.cttForm = this.fb.group({
			contact_email: [null, [Validators.required, Validators.email]],
			contact_name: [null, [Validators.required]],
			contact_phone: [null],
		});
		this.addForm = this.fb.group({
			city: [null, [Validators.required]],
			complement: [null],
			neighborhood: [null, [Validators.required]],
			number: [null, [Validators.required]],
			postal_code: [
				null,
				[Validators.required, Validators.pattern(this.cepPattern)],
			],
			state: [null, [Validators.required]],
			street: [null, [Validators.required]],
		});
		this.addForm.get('state')!.disable();
		this.addForm.get('city')!.disable();

		this.productsForm = this.fb.group({
			products: [null],
		});
	}

	configureProducts(products: ProductsSummary[]) {
		const ids = products.map((p: ProductsSummary) => p.id);
		const names = products.map((p: ProductsSummary) => p.name);
		this.productsIds = ids;
		this.productsInput = names.join(', ');
	}

	displayHqFn(hq: HeadquarterSummary): string {
		return hq?.id ? `${hq.name} (${hq.cnpj})` : '';
	}

	ngOnDestroy(): void {
		if (this.organizations$) {
			this.organizations$.unsubscribe();
		}
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		this.store.dispatch(fetchUFCitiesClear());
		this.store.dispatch(restoreFormReset());
	}

	ngOnInit(): void {
		this.activatedRoute.queryParams
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((params) => {
				this.applyReconciliationSubPrefill(params);
			});

		this.activatedRoute.params.subscribe(({ id }) => {
			if (!id) {
				this.isLoading = false;
			} else {
				this.orgId = id;
				this.store.dispatch(fetchOrgRegStart({ id }));
				this.store
					.select(selectOrgRegistration)
					.pipe(takeUntil(this.unsubscribe$))
					.subscribe((o) => {
						if (o) {
							this.regForm = this.fb.group({
								cnpj: [
									{ disabled: true, value: o.cnpj },
									[
										Validators.required,
										Validators.minLength(1),
										Validators.pattern(this.cnpjPattern),
									],
								],
								insurancePolicyNumber: [o.insurancePolicyNumber],
								is_matriz: [o.is_matriz, [Validators.required]],
								matriz_id: [o.matriz_id],
								municipal_registration: [o.municipal_registration],
								name: [o.name, [Validators.required]],
								name_economic_group: [o.name_economic_group],
								name_secondary: [o.name_secondary, [Validators.required]],
								state_registration: [o.state_registration],
								subinvoiceNumber: [o.subinvoiceNumber],
							});
							this.cttForm = this.fb.group({
								contact_email: [o.contact_email],
								contact_name: [o.contact_name],
								contact_phone: [o.contact_phone],
							});
							this.addForm = this.fb.group({
								city: [o.address?.city ?? null],
								complement: [o.address?.complement ?? null],
								neighborhood: [
									o.address?.neighborhood ?? null,
									[Validators.required],
								],
								number: [o.address?.number ?? null, [Validators.required]],
								postal_code: [
									o.address?.postal_code?.replace('-', '') ?? null,
									[Validators.required, Validators.pattern(this.cepPattern)],
								],
								state: [o.address?.state ?? null],
								street: [o.address?.street ?? null, [Validators.required]],
							});
							this.addForm.get('state')!.disable();
							this.addForm.get('city')!.disable();
							this.onRadioChange(o.is_matriz);
							this.productsForm = this.fb.group({
								products: [o.products],
							});

							if (o.matriz_id && !o.is_matriz) {
								this.fetchAndSetInitialMatrizDisplay(o.matriz_id);
							}

							if (o.products) {
								this.configureProducts(o.products);
							}
							this.isLoading = false;
						}
					});
			}
		});
		this.store
			.select(selectCities)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((cities) => {
				if (this.auxStateForm && cities.length) {
					this.addForm.get('city')!.enable();
				}
				this.cities = cities;
			});
		this.store.dispatch(fetchHeadquartersStart());

		this.filteredHeadquarters$ = this.matrizDisplayCtrl.valueChanges.pipe(
			startWith(''),
			debounceTime(300),
			distinctUntilChanged(),
			switchMap((value) => {
				if (value && typeof value !== 'string' && 'id' in value) {
					return of([value]);
				}

				return this._searchHeadquarters(value || '');
			}),
		);

		this.regForm
			.get('matriz_id')
			?.valueChanges.pipe(
				takeUntil(this.unsubscribe$),
				filter((id) => !id),
			)
			.subscribe(() => {
				this.matrizDisplayCtrl.setValue('');
			});

		this.searchProducts();
	}

	onCepBlur(): void {
		if (this.addForm.get('postal_code')!.valid) {
			const cep: string = this.addForm.get('postal_code')!.value;
			this.store.dispatch(fetchAddressByCEPStart({ cep }));
			this.store
				.select(selectAddressResult)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((result) => {
					if (result?.error) {
						this.store.dispatch(fetchUFCitiesClear());
						this.auxStateForm = true;
						this.addForm.get('state')!.setValue(null);
						this.addForm.get('city')!.setValue(null);
						this.addForm.get('state')!.enable();
					} else {
						this.auxStateForm = false;
						this.addForm.get('street')!.setValue(result?.street);
						this.addForm.get('neighborhood')!.setValue(result?.neighborhood);
						this.addForm.get('state')!.setValue(result?.state);
						this.addForm.get('city')!.setValue(result?.city);
						this.addForm.get('state')!.disable();
						this.addForm.get('city')!.disable();
					}
				});
		}
	}

	onCnpjBlur(): void {
		if (this.regForm.get('cnpj')!.valid) {
			const cnpj: string = this.regForm.get('cnpj')!.value;
			this.store.dispatch(
				fetchCnpjAvailabilityStart({ category: 'organization', cnpj }),
			);
			this.store
				.select(selectCnpjAvailability)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((result) => {
					if (!result) {
						this.regForm.get('cnpj')!.setErrors({ minlength: true });
					} else {
						this.regForm.get('cnpj')!.updateValueAndValidity();
					}
				});
		}
	}

	onHqSelected(event: MatAutocompleteSelectedEvent): void {
		const selectedHq: HeadquarterSummary = event.option.value;
		this.regForm.patchValue({ matriz_id: selectedHq.id });
	}

	onRadioChange(isMatriz: boolean): void {
		if (!isMatriz) {
			this.regForm.get('matriz_id')!.setValidators([Validators.required]);
		} else {
			this.regForm.get('matriz_id')!.setValue(null);
			this.regForm.get('matriz_id')!.setValidators([]);
			this.matrizDisplayCtrl.setValue('');
		}
		this.regForm.get('matriz_id')!.updateValueAndValidity();
		this.showEconomicGroup = isMatriz;
		if (isMatriz) {
			this.regForm.get('name_economic_group')!.setValue(null);
		}
	}

	onSubmit(): void {
		if (
			!(this.regForm.invalid || this.addForm.invalid || this.cttForm.invalid)
		) {
			this.store
				.select(selectRestoreForm)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((restore) => {
					if (restore) {
						this.isLoading = false;
						this.store.dispatch(restoreFormReset());
					}
				});
			this.addForm.enable();
			this.regForm.get('cnpj')!.enable();
			const base = this.regForm.value;
			const contact = this.cttForm.value;
			const address = this.addForm.value;
			address.postal_code = address.postal_code.replace('-', '');
			this.addForm.get('state')!.disable();
			this.addForm.get('city')!.disable();
			const request: NewOrganizationRequest = {
				...base,
				address,
				contact_email: contact.contact_email,
				contact_name: contact.contact_name,
				contact_phone: contact.contact_phone,
				productIds: this.productsIds,
			};

			if (this.orgId) {
				this.regForm.get('cnpj')!.disable();
				this.store.dispatch(editOrgRegStart({ id: this.orgId, request }));
			} else {
				this.store.dispatch(
					createOrgStart({
						redirectUrl: this.reconciliationReturnUrl ?? undefined,
						request,
					}),
				);
			}
			this.isLoading = true;
		}
	}

	onUFChange(id: number): void {
		this.addForm.get('city')!.setValue(null);
		this.addForm.get('city')!.disable();
		this.store.dispatch(fetchUFCitiesStart({ id }));
	}

	openModal() {
		const dialogRef = this.dialog.open(ProductsModalComponent, {
			data: { products: this.products, selectedProducts: this.productsIds },
			maxWidth: undefined,
		});

		dialogRef.afterClosed().subscribe((result) => {
			this.configureProducts(result);
		});
	}

	searchProducts(): void {
		this.store.dispatch(fetchOrgProducts());
		this.store
			.select(selectOrgProducts)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((products) => {
				if (products.length > 0) {
					this.products = products;
				}
			});
	}

	shouldDisableSaveBtn(): boolean {
		return this.regForm.invalid || this.cttForm.invalid || this.addForm.invalid;
	}

	private _searchHeadquarters(term: string): Observable<HeadquarterSummary[]> {
		const where = JSON.stringify({
			OR: [
				{ name_secondary: { contains: term, mode: 'insensitive' } },
				{ cnpj: { contains: term, mode: 'insensitive' } },
			],
			active: true,
			is_matriz: true,
		});

		return this.organizationsService
			.findMany(
				new PaginationArgs({
					limit: 20,
					orderBy: { name_secondary: SortingType.ASCENDING },
					page: 1,
					select: { id: true, cnpj: true, name_secondary: true },
					where: where,
				}),
			)
			.pipe(
				map((response) =>
					response.result.map(
						(element): HeadquarterSummary => ({
							id: element.id,
							cnpj: element.cnpj,
							name: element.name_secondary,
						}),
					),
				),
			);
	}

	private applyReconciliationSubPrefill(params: {
		[key: string]: string | undefined;
	}): void {
		const cnpj = params.reconciliationCnpj;
		const matrizId = params.reconciliationMatrizOrganizationId;
		const returnOrganizationId = params.reconciliationReturnOrganizationId;
		const sessionId = params.reconciliationSessionId;

		if (!cnpj || !matrizId || !returnOrganizationId || !sessionId) {
			return;
		}

		this.regForm.patchValue({
			cnpj,
			is_matriz: false,
			matriz_id: matrizId,
		});
		this.onRadioChange(false);
		this.fetchAndSetInitialMatrizDisplay(matrizId);
		this.reconciliationReturnUrl = `/organizacoes/${returnOrganizationId}/reconciliacao-beneficiarios?comparisonId=${sessionId}&reprocessPreflight=true`;
	}

	private fetchAndSetInitialMatrizDisplay(hqId: string): void {
		this.organizationsService.getById(hqId).subscribe((hqData) => {
			if (hqData) {
				const hqSummary: HeadquarterSummary = {
					id: hqData.id,
					cnpj: hqData.cnpj,
					name: hqData.name_secondary,
				};
				this.matrizDisplayCtrl.setValue(hqSummary, { emitEvent: false });
			}
		});
	}
}
